const CODE_SPAN_OR_FENCE = /```[\s\S]*?(?:```|$)|~~~[\s\S]*?(?:~~~|$)|`[^`\n]*(?:`|$)/g;

const isWhitespace = (character: string | undefined): boolean =>
  character !== undefined && /\s/.test(character);

const isDigit = (character: string | undefined): boolean =>
  character !== undefined && /[0-9]/.test(character);

const isEscapedDollar = (source: string, index: number): boolean => {
  let backslashCount = 0;
  for (let cursor = index - 1; cursor >= 0 && source[cursor] === '\\'; cursor -= 1) {
    backslashCount += 1;
  }
  return backslashCount % 2 === 1;
};

const canOpenSingleDollar = (source: string, index: number): boolean => {
  const next = source[index + 1];
  return !isEscapedDollar(source, index)
    && next !== undefined
    && next !== '$'
    && !isWhitespace(next);
};

const canCloseSingleDollar = (source: string, index: number): boolean => {
  const previous = source[index - 1];
  const next = source[index + 1];
  return !isEscapedDollar(source, index)
    && previous !== undefined
    && !isWhitespace(previous)
    && !isDigit(next);
};

const isPlausibleInlineLatex = (content: string): boolean => {
  const trimmed = content.trim();
  if (!trimmed) return false;

  if (/[\\^_=+\-*/<>]/.test(trimmed)) return true;

  // Match iOS: a plain single token such as $x$, $n$, or $5$ is valid
  // inline math, while multi-word prose between currency amounts is not.
  return !/\s/.test(trimmed);
};

const findClosingDoubleDollar = (source: string, startIndex: number): number => {
  for (let cursor = startIndex; cursor < source.length - 1; cursor += 1) {
    if (
      source[cursor] === '$'
      && source[cursor + 1] === '$'
      && !isEscapedDollar(source, cursor)
    ) {
      return cursor;
    }
  }
  return -1;
};

const findClosingSingleDollar = (source: string, startIndex: number): number => {
  for (let cursor = startIndex; cursor < source.length; cursor += 1) {
    if (source[cursor] !== '$' || !canCloseSingleDollar(source, cursor)) continue;

    // A dollar that begins a `$$` pair cannot close single-dollar math.
    if (source[cursor + 1] === '$') {
      cursor += 1;
      continue;
    }
    return cursor;
  }
  return -1;
};

const preprocessTextLatex = (content: string): string => {
  let output = '';
  let index = 0;

  while (index < content.length) {
    // `\( ... \)` is the other inline delimiter accepted by the iOS renderer.
    if (content.startsWith('\\(', index)) {
      const closeIndex = content.indexOf('\\)', index + 2);
      if (closeIndex !== -1) {
        output += `$${content.slice(index + 2, closeIndex)}$`;
        index = closeIndex + 2;
        continue;
      }
    }

    if (content[index] !== '$' || isEscapedDollar(content, index)) {
      output += content[index];
      index += 1;
      continue;
    }

    if (content[index + 1] === '$') {
      const closeIndex = findClosingDoubleDollar(content, index + 2);
      if (closeIndex !== -1) {
        output += content.slice(index, closeIndex + 2);
        index = closeIndex + 2;
        continue;
      }

      // Keep an unfinished display delimiter intact while content streams.
      output += '$$';
      index += 2;
      continue;
    }

    if (canOpenSingleDollar(content, index)) {
      const closeIndex = findClosingSingleDollar(content, index + 1);
      if (
        closeIndex !== -1
        && isPlausibleInlineLatex(content.slice(index + 1, closeIndex))
      ) {
        output += content.slice(index, closeIndex + 1);
        index = closeIndex + 1;
        continue;
      }
    }

    // Escaping only for the Markdown parser makes this dollar render literally.
    output += '\\$';
    index += 1;
  }

  return output;
};

/**
 * Applies the same inline-math rules as iOS without changing fenced or inline
 * code. A lone `$` is literal; inline math needs a plausible paired `$...$`
 * span (or `\(...\)`), and an explicit `\$` remains a literal dollar.
 */
export const preprocessLatex = (content: string): string => {
  let output = '';
  let previousEnd = 0;

  for (const match of content.matchAll(CODE_SPAN_OR_FENCE)) {
    const matchIndex = match.index ?? 0;
    output += preprocessTextLatex(content.slice(previousEnd, matchIndex));
    output += match[0];
    previousEnd = matchIndex + match[0].length;
  }

  output += preprocessTextLatex(content.slice(previousEnd));
  return output;
};
